import { useEffect, useRef, useState } from 'react';

const teamMembers = [
  {
    role: 'Hairdresser',
    title: '集客支援事業　美容師歴21年+',
    subtitle: '現場の痛みと、顧客の心理を知り尽くす。',
    desc: '21年間の現場経験から培った、顧客心理の深い洞察。業界や現場をわかっているからこそリアルな視点があります',
    icon: 'ri-scissors-line',
    accent: '#b4a078',
    img: 'https://static.readdy.ai/image/d40b343a735e9ff1d1cb56d3b54df611/7e66200bbda48c99dae73804859624d8.png',
  },
  {
    role: 'Marketer & EC Specialist',
    title: 'EC構築事業　累計20億の実績',
    subtitle: '勝てるロジックで、市場を制する。',
    desc: 'EC売上20億円、ランキング900冠以上。数字で証明された戦略とともに、あなたの新商品を市場へと押し上げます。',
    icon: 'ri-line-chart-line',
    accent: '#7a9e6a',
    img: 'https://public.readdy.ai/ai/img_res/7bb3acfc0def5e97c9a0bc3b0afca85c.jpg',
  },
  {
    role: 'AI Consultant',
    title: 'AI活用事業　バックオフィス自動化',
    subtitle: 'スタッフに「時間」という最高の贈り物を。',
    desc: '最新AIツールを駆使し、繰り返し業務を自動化。あなたのチームが本当に価値ある仕事に集中できる環境を構築します。',
    icon: 'ri-robot-line',
    accent: '#6a8e9e',
    img: 'https://readdy.ai/api/search-image?query=clean-bright-ai-technology-neural-network-visualization-abstract-digital-art-white-light-background-soft-gold-teal-particles-sophisticated-minimalist-airy&width=600&height=400&seq=team013&orientation=landscape',
  },
  {
    role: 'Grants & Financial Consultant',
    title: '補助金事業　新たな事業をブーストする補助金戦略',
    subtitle: '攻めの財務基盤で、成長を加速させる。',
    desc: '補助金・助成金の活用から戦略策定から実行まで。制度を最大限に活かし、リスクを抑えながら積極的な事業拡大を実現します。',
    icon: 'ri-government-line',
    accent: '#9e8a6a',
    img: 'https://readdy.ai/api/search-image?query=bright-luxury-financial-documents-gold-coins-elegant-light-background-sophisticated-business-finance-minimalist-japanese-aesthetic-premium-quality-clean-white&width=600&height=400&seq=team014&orientation=landscape',
  },
  {
    role: 'Designer',
    title: 'デザイン事業　ブランドの格を上げる',
    subtitle: 'もうデザイン業務に悩まない。　美容業界に精通したデザイン事業部',
    desc: 'ブランドの世界観を視覚言語に変換。一目で「格」が伝わるデザインが、顧客の心に刻まれ、長期的なファンを生み出します。',
    icon: 'ri-palette-line',
    accent: '#9e6a8e',
    img: 'https://readdy.ai/api/search-image?query=bright-luxury-brand-design-studio-light-elegant-mood-board-typography-gold-cream-minimalist-japanese-aesthetic-creative-workspace-sophisticated-clean-airy&width=600&height=400&seq=team015&orientation=landscape',
  },
];

function TeamCard({ member, index }: { member: typeof teamMembers[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden rounded-2xl bg-white border border-[#1a1a1a]/8 hover:border-[#b4a078]/40 transition-all duration-700 cursor-pointer ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={member.img}
          alt={member.role}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
        {/* Role badge */}
        <div className="absolute top-4 left-4">
          <span
            className="text-[9px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full border bg-white/80 backdrop-blur-sm"
            style={{ color: member.accent, borderColor: `${member.accent}50` }}
          >
            {member.role}
          </span>
        </div>
        {/* Icon */}
        <div
          className="absolute bottom-4 right-4 w-10 h-10 flex items-center justify-center rounded-full border bg-white/80 backdrop-blur-sm"
          style={{ borderColor: `${member.accent}40` }}
        >
          <i className={`${member.icon} text-base`} style={{ color: member.accent }} />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-[#1a1a1a] text-base font-light mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
          {member.title}
        </h3>
        <p className="text-[#b4a078] text-xs tracking-wide mb-3">{member.subtitle}</p>
        <p className="text-[#1a1a1a]/45 text-xs leading-relaxed">{member.desc}</p>

        {/* Hover line */}
        <div
          className="mt-5 h-px w-0 group-hover:w-full transition-all duration-500"
          style={{ background: `linear-gradient(to right, ${member.accent}, transparent)` }}
        />
      </div>
    </div>
  );
}

export default function TeamSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" className="relative bg-[#fafaf8] py-24 md:py-36 overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 80px, rgba(180,160,120,1) 80px, rgba(180,160,120,1) 81px), repeating-linear-gradient(90deg, transparent, transparent 80px, rgba(180,160,120,1) 80px, rgba(180,160,120,1) 81px)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div
          ref={titleRef}
          className={`mb-16 md:mb-20 transition-all duration-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-[#b4a078]" />
            <span className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase">The Core Strength</span>
          </div>
          <h2
            className="text-[#1a1a1a] font-light leading-tight mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif", fontSize: 'clamp(1.8rem, 4vw, 3.5rem)' }}
          >
            私たちのチーム体制
          </h2>
          <p className="text-[#1a1a1a]/45 text-sm leading-relaxed max-w-xl">
            各分野のスペシャリストが、あなたの事業の「右腕」になります。<br />
            一過性のコンサルではなく、成功を共有する実戦集団。
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {teamMembers.slice(0, 3).map((member, i) => (
            <TeamCard key={member.role} member={member} index={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5 lg:grid-cols-2 lg:max-w-[66.666%]">
          {teamMembers.slice(3).map((member, i) => (
            <TeamCard key={member.role} member={member} index={i + 3} />
          ))}
        </div>

        {/* Core value quote */}
        <div
          className={`mt-20 border-t border-[#1a1a1a]/8 pt-16 transition-all duration-1000 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '600ms' }}
        >
          <blockquote
            className="text-[#1a1a1a]/20 font-light text-center"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.2rem, 3vw, 2.2rem)' }}
          >
            &ldquo;一人では辿り着けない場所へ、最強のチームと。&rdquo;
          </blockquote>
        </div>
      </div>
    </section>
  );
}
